<script lang="ts" setup>
import { inject, onBeforeUnmount } from 'vue'
import { modalsKey } from '../injectionKeys'
const emit = defineEmits(['close'])
const scrollBlocker = document.createElement('style')
scrollBlocker.innerText = 'html { overflow: hidden; }'
document.body.appendChild(scrollBlocker)
const modals = inject(modalsKey)?.value
const modalSymol = Symbol('modal')
if (modals) {
  modals?.push(modalSymol)
}
const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    e.preventDefault()
    e.stopPropagation()
    emit('close')
  }
}
document.addEventListener('keydown', onKeydown)
onBeforeUnmount(() => {
  if (scrollBlocker.parentNode) {
    scrollBlocker.parentNode.removeChild(scrollBlocker)
  }
  if (modals) {
    const i = modals.findIndex(v => v === modalSymol)
    modals.splice(i, 1)
  }
  document.removeEventListener('keydown', onKeydown)
})
let pointerdownTarget: EventTarget | null = null
const onPointerDownOutside = (e: PointerEvent) => {
  pointerdownTarget = e.target
}
const onPointerUpOutside = (e: PointerEvent) => {
  if (e.currentTarget === e.target && pointerdownTarget === e.target) emit('close')
}
</script>

<template>
  <div class="Modal" @pointerdown="onPointerDownOutside" @pointerup="onPointerUpOutside">
    <div class="ModalContainer">
      <div class="ModalContainer-inner" @click.stop>
        <slot />
      </div>
      <i class="ModalContainer-close material-symbols-rounded" @click="$emit('close')">cancel</i>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.material-symbols-rounded {
  font-variation-settings: 'FILL' 1;
}
.Modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10001;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 15px;
}
.ModalContainer {
  display: flex;
  position: relative;
  min-width: 320px;
  max-width: 640px;
  max-height: unquote('min(90vh, 800px)');
  max-height: unquote('min(90dvh, 800px)');
  background: var(--bg-color);
  border-radius: var(--radius);
  font-weight: normal;
  color: var(--tx-color);
  box-shadow: 0 0 7px rgba(0, 0, 0, 0.3);
  &-inner {
    flex: 1;
    overflow-x: hidden;
    overflow-y: auto;
  }
  &-close {
    font-size: 30px;
    background-color: var(--bg-color);
    border-radius: 20px;
    position: absolute;
    top: -7px;
    right: -7px;
    cursor: pointer;
  }
}
::v-deep(.ModalContainer-inner) {
  .ModalContent {
    padding: 12px;
    :not(button, .button) {
      user-select: text;
    }
  }
  .ModalTitle {
    margin-bottom: 10px;
    font-size: 15px;
    font-weight: bold;
    overflow-wrap: anywhere;
  }
  p {
    margin: 1em 0.5em;
  }
}
</style>

<style lang="scss">
.modal-enter-active,
.modal-leave-active {
  transition: 0.2s;
  .ModalContainer {
    transition: 0.2s;
  }
}
.modal-enter-active {
  .ModalContainer {
    transition-timing-function: cubic-bezier(.02,.73,.49,1.24);
  }
}
.modal-leave-active {
  .ModalContainer {
    transition-timing-function: ease-in;
  }
}
.modal-enter-from,
.modal-leave-to {
  .ModalContainer {
    transform: scale(0);
  }
}
.modal-enter-to,
.modal-leave-from {
  .ModalContainer {
    transform: scale(1);
  }
}
</style>
